import { BaseShader } from "webgl-framework";
import { ShaderInputs } from "./ShaderInputs";
import { engine } from "../KotlinLib";

/** Leaves of the texture atlas are laid out in a 3x3 grid. */
const ATLAS_SIZE = 3;

/**
 * Shader of the leaves of the particle system: one cell of the leaves atlas, faded into the
 * distance by the same linear fog as the main shader.
 *
 * The quad of a leaf spans the whole atlas, so its texture coordinates are squeezed into one
 * cell and moved to the cell of this leaf. The leave is not lit: the atlas holds finished
 * leaves, so only fog is applied to them.
 *
 * Uniforms: [view_proj_matrix, textureOffset, sTexture, fogColor, fogStartDistance,
 * fogDistance] - the order the particle system puts them into its draw commands.
 */
export class LeavesShader extends BaseShader implements ShaderInputs {
    view_proj_matrix: WebGLUniformLocation | undefined;
    textureOffset: WebGLUniformLocation | undefined;
    sTexture: WebGLUniformLocation | undefined;
    fogColor: WebGLUniformLocation | undefined;
    fogStartDistance: WebGLUniformLocation | undefined;
    fogDistance: WebGLUniformLocation | undefined;

    rm_Vertex: number | undefined;
    rm_TexCoord0: number | undefined;

    public attributes?: number[];
    public typedAttributes?: { [key: number]: number };
    public uniforms?: WebGLUniformLocation[];

    /** @inheritdoc */
    fillCode() {
        this.vertexShaderCode = `precision highp float;
            uniform mat4 view_proj_matrix;
            uniform float fogStartDistance;
            uniform float fogDistance;
            attribute vec4 rm_Vertex;
            attribute vec2 rm_TexCoord0;
            varying vec2 vTextureCoord;
            varying float vFogAmount;

            void main() {
              gl_Position = view_proj_matrix * rm_Vertex;
              vTextureCoord = rm_TexCoord0;
              vFogAmount = clamp((length(gl_Position) - fogStartDistance) / fogDistance, 0.0, 1.0);
            }`;

        this.fragmentShaderCode = `precision mediump float;
            varying vec2 vTextureCoord;
            varying float vFogAmount;

            uniform sampler2D sTexture;
            uniform vec2 textureOffset;
            uniform vec4 fogColor;

            void main() {
              vec2 textureCoord = vTextureCoord / ${ATLAS_SIZE}.0 + textureOffset;
              vec4 color = texture2D(sTexture, textureCoord);
              if (color.a < 0.5) {
                discard;
              }

              gl_FragColor = mix(vec4(color.rgb, 1.0), fogColor, vFogAmount);
            }`;
    }

    /** @inheritdoc */
    fillUniformsAttributes() {
        this.rm_Vertex = this.getAttrib("rm_Vertex");
        this.rm_TexCoord0 = this.getAttrib("rm_TexCoord0");

        this.view_proj_matrix = this.getUniform("view_proj_matrix");
        this.textureOffset = this.getUniform("textureOffset");
        this.sTexture = this.getUniform("sTexture");
        this.fogColor = this.getUniform("fogColor");
        this.fogStartDistance = this.getUniform("fogStartDistance");
        this.fogDistance = this.getUniform("fogDistance");

        this.attributes = [this.rm_Vertex, this.rm_TexCoord0];
        this.typedAttributes = {
            [engine.AttributeType.VERTEX.ordinal]: this.rm_Vertex!,
            [engine.AttributeType.UV0.ordinal]: this.rm_TexCoord0!
        };
        this.uniforms = [
            this.view_proj_matrix,
            this.textureOffset,
            this.sTexture,
            this.fogColor,
            this.fogStartDistance,
            this.fogDistance
        ];
    }
}
